import { useEffect, useRef } from "react";
import { trapDialogTab } from "./week39-dialog";
import { Week39Icon } from "./Week39Icon";
import { useWeek39Recipe, type RecipeNode } from "./useWeek39Recipe";
import "./week39-recipe.css";

const quantityFormatter = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 6 });
function NodeQuantity({ node, root }: { node: RecipeNode; root: boolean }) {
  if (root) return null;
  if (node.quantity !== null && node.unit !== null && node.parentQuantity !== null && node.parentUnit !== null) {
    return <small className="pack39-recipe-quantity">{quantityFormatter.format(node.quantity)} {node.unit} / {quantityFormatter.format(node.parentQuantity)} {node.parentUnit}</small>;
  }
  return <small className="pack39-recipe-quantity pack39-recipe-quantity-unknown">Miktar veya birim doğrulanamadı</small>;
}

function TreeNode({ node, root = false }: { node: RecipeNode; root?: boolean }) {
  return <li className={`pack39-recipe-node${root ? " pack39-recipe-root" : ""}`}>
    {node.children.length ? <details open={root}><summary><strong>{node.code}</strong><span className="pack39-recipe-name">{node.name}</span><NodeQuantity node={node} root={root} /><small className="pack39-recipe-count">{node.children.length} bileşen</small></summary>
      <ul>{node.children.map((child, index) => <TreeNode key={`${child.code}-${index}`} node={child} />)}</ul></details>
      : <div className="pack39-recipe-leaf"><strong>{node.code}</strong><span className="pack39-recipe-name">{node.name}</span><NodeQuantity node={node} root={root} />
        {node.cycle ? <small>Döngü: dal durduruldu</small> : node.incomplete ? <small>Bu dalın devamı alınamadı</small> : null}</div>}
  </li>;
}

export function Week39RecipePanel({ code, weekStart, onClose }: { code: string; weekStart?: string; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { recipe, error, loading, reload } = useWeek39Recipe(code, weekStart);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = overflow; };
  }, []);
  return <dialog ref={dialogRef} className="pack39-dialog pack39-recipe-panel" aria-labelledby="pack39-recipe-title" aria-modal="true" onKeyDown={trapDialogTab} onClose={onClose}>
    <header className="pack39-dialog-header"><div><h2 id="pack39-recipe-title">Ürün reçetesi</h2><p>{code} · Mikro ERP</p></div>
      <button className="pack39-icon-button" type="button" onClick={() => dialogRef.current?.close()} aria-label="Reçeteyi kapat"><Week39Icon name="close" size={21} /></button></header>
    <div className="pack39-recipe-body" aria-live="polite">
      {loading ? <p className="pack39-recipe-state">Mikro reçetesi okunuyor…</p> : null}
      {error ? <div className="pack39-recipe-state" role="alert"><p>{error}</p><button className="pack39-button" type="button" onClick={reload}>Yeniden dene</button></div> : null}
      {recipe && !recipe.root.children.length ? <p className="pack39-recipe-state">Bu ürün için Mikro’da aktif reçete satırı bulunamadı.</p> : null}
      {recipe?.truncated ? <p className="pack39-recipe-warning" role="status">Reçete sınırına ulaşıldı. Bazı alt dallar gösterilemiyor.</p> : null}
      {recipe?.root.children.length ? <ul className="pack39-recipe-tree"><TreeNode node={recipe.root} root /></ul> : null}
    </div>
    {recipe ? <footer className="pack39-recipe-footer">Miktarlar her satırda doğrudan üst ürünün reçete bazına göredir. Mikro’dan okundu: {new Date(recipe.observedAt).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" })}.</footer> : null}
  </dialog>;
}
